import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { TeamEntity } from './team.entity';

/**
 * Which client field a rule tests. A closed list on purpose: each one maps to
 * a known column (or, for `branch`, a known join), so a rule can never be
 * turned into arbitrary SQL by a caller.
 */
export const TEAM_RULE_FIELDS = [
	'state',
	'zip',
	'city',
	'county',
	'country',
	'branch',
	'lifecycleStage',
	'recordType',
] as const;

export type TeamRuleFieldModel = (typeof TEAM_RULE_FIELDS)[number];

export const TEAM_RULE_OPERATORS = ['in', 'notIn'] as const;

export type TeamRuleOperatorModel = (typeof TEAM_RULE_OPERATORS)[number];

/**
 * One criterion deciding which clients a team covers (PD-16310).
 *
 * Several rules on one team are ANDed; the values WITHIN a rule are ORed. So
 * "state in (GA)" is one rule, and "eastern states AND customers only" is two.
 * That covers the shapes tenants actually use — Candor's `GA01` is a single
 * `state in (GA)` — without a nested boolean builder nobody can read.
 *
 * Rules INHERIT down the team tree: a child's effective criteria are its own
 * ANDed with every ancestor's, so "Northeast" narrows "East Coast" rather than
 * restating it.
 */
@Index('team_client_rule__fk_team__id', ['fkTeamId'], {})
@Entity('team_client_rule')
export class TeamClientRuleEntity extends BaseEntity {
	@Column('int', { name: 'fk_team__id', unsigned: true })
	public fkTeamId!: number;

	@ManyToOne(() => TeamEntity, (team) => team.clientRules, {
		onDelete: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_team__id', referencedColumnName: 'id' }])
	public fkTeam!: TeamEntity;

	@Column({
		name: 'field',
		type: 'enum',
		enum: TEAM_RULE_FIELDS,
	})
	public field!: TeamRuleFieldModel;

	@Column({
		name: 'operator',
		type: 'enum',
		enum: TEAM_RULE_OPERATORS,
		default: "'in'",
	})
	public operator!: TeamRuleOperatorModel;

	/**
	 * The values this rule matches, as a JSON array of strings. Stored as JSON
	 * rather than a child table because a rule's values are only ever read and
	 * written together, and a five-row join to express "twelve states" earns
	 * nothing.
	 */
	@Column('json', { name: 'ruleValues' })
	public ruleValues!: Array<string>;
}
